import React from 'react';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { FormProvider, RHFDateRangePickerField, RHFSelectField, RHFTextField } from '../../../common/hook-form';
import { CourierList } from '../../../common/hook-form/CourierFilter';
import { ATTEMPT_OPTIONS, DOCUMENTS_OPTIONS, MISS_CATEGORY_OPTIONS, getDefaultDateRange } from './constants';

function FieldLabel({ children }) {
    return (
        <Typography sx={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: '#888',
            fontWeight: 600,
            mb: 0.5,
            display: 'block',
            lineHeight: 1.2,
        }}>{children}</Typography>
    );
}

export default function FilterBar({
    show = ['courier', 'awb', 'city', 'attempts', 'missCategory', 'dateRange'],
    onApply,
    onReset,
    initial = {},
    // Extra select filters: [{ name, label, options: [{ value, label }] }]; their values come back in onApply.
    selects = [],
}) {
    const def = getDefaultDateRange();
    const selectDefaults = Object.fromEntries(selects.map((f) => [f.name, '']));
    const methods = useForm({
        defaultValues: {
            courier: '',
            awb: '',
            reference: '',
            clientOrderId: '',
            city: '',
            state: '',
            attempts: '',
            missCategory: '',
            documents: '',
            fromDate: def.fromDate,
            toDate: def.toDate,
            ...selectDefaults,
            ...initial,
        },
    });
    const { handleSubmit, reset } = methods;

    const submit = (data) => {
        onApply({
            courier: data.courier || '',
            awb: data.awb,
            reference: data.reference,
            clientOrderId: data.clientOrderId,
            city: data.city,
            state: data.state,
            attempts: data.attempts,
            missCategory: data.missCategory,
            documents: data.documents || '',
            fromDate: data.fromDate || null,
            toDate: data.toDate || null,
            ...Object.fromEntries(selects.map((f) => [f.name, data[f.name] || ''])),
        });
    };

    const resetAll = () => {
        const fresh = {
            courier: '',
            awb: '',
            reference: '',
            clientOrderId: '',
            city: '',
            state: '',
            attempts: '',
            missCategory: '',
            documents: '',
            fromDate: def.fromDate,
            toDate: def.toDate,
            ...selectDefaults,
            ...initial,
        };
        reset(fresh);
        onReset?.();
    };

    const visible = (k) => show.includes(k);

    const fieldSx = { flex: '1 1 140px', minWidth: 140 };
    const dateSx = { flex: '0 0 260px', minWidth: 260 };
    const inputSx = {
        fontFamily: 'Poppins, sans-serif',
        '& .MuiOutlinedInput-root': {
            fontSize: '12px', fontFamily: 'Poppins, sans-serif',
            backgroundColor: '#fff',
            borderRadius: '4px',
            minHeight: '33px',
            height: '33px',
        },
        '& .MuiOutlinedInput-input': { padding: '8px 10px', fontSize: '12px', color: '#333', height: 'auto' },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#ccc',
            top: 0,
            '& legend': { display: 'none' },
        },
        '& .MuiInputLabel-root': { display: 'none' },
        '& .MuiFormHelperText-root': { display: 'none' },
        '& input::placeholder, & .MuiInputBase-input::placeholder': { opacity: '1 !important', color: '#999', fontSize: '12px' },
        '& .MuiOutlinedInput-input::placeholder': { opacity: '1 !important', color: '#999' },
        '& .MuiSelect-select .MuiTypography-root': { fontSize: '12px', color: '#999' },
        '& .MuiSelect-select': {
            padding: '8px 32px 8px 10px !important',
            minHeight: '0 !important',
            height: 'auto',
            lineHeight: '17px',
            boxSizing: 'border-box',
        },
        '& .MuiAutocomplete-inputRoot': { padding: '0 35px 0 4px !important' },
        '& .MuiAutocomplete-input': { padding: '8px 10px !important' },
    };

    return (
        <Card sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <FormProvider methods={methods} onSubmit={handleSubmit(submit)}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>
                        {visible('courier') && (
                            <Box sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>Courier</FieldLabel>
                                <CourierList filterList='B2B' fullWidth size='small' label='' placeholder='Select Courier' />
                            </Box>
                        )}
                        {visible('reference') && (
                            <Box sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>Order Number</FieldLabel>
                                <RHFTextField name='reference' label='' placeholder='Order Number' fullWidth size='small' InputLabelProps={{ shrink: true }} />
                            </Box>
                        )}
                        {visible('awb') && (
                            <Box sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>AWB Number</FieldLabel>
                                <RHFTextField name='awb' label='' placeholder='AWB Number' fullWidth size='small' InputLabelProps={{ shrink: true }} />
                            </Box>
                        )}
                        {visible('clientOrderId') && (
                            <Box sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>Client Order ID</FieldLabel>
                                <RHFTextField name='clientOrderId' label='' placeholder='Client Order ID' fullWidth size='small' InputLabelProps={{ shrink: true }} />
                            </Box>
                        )}
                        {visible('city') && (
                            <Box sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>City</FieldLabel>
                                <RHFTextField name='city' label='' placeholder='City' fullWidth size='small' InputLabelProps={{ shrink: true }} />
                            </Box>
                        )}
                        {visible('state') && (
                            <Box sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>State</FieldLabel>
                                <RHFTextField name='state' label='' placeholder='State' fullWidth size='small' InputLabelProps={{ shrink: true }} />
                            </Box>
                        )}
                        {visible('attempts') && (
                            <Box sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>Attempts</FieldLabel>
                                <RHFSelectField name='attempts' label='' placeholder='All' width='100%' size='small' displayEmpty options={ATTEMPT_OPTIONS} />
                            </Box>
                        )}
                        {visible('missCategory') && (
                            <Box sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>Miss Category</FieldLabel>
                                <RHFSelectField name='missCategory' label='' placeholder='All' width='100%' size='small' displayEmpty options={MISS_CATEGORY_OPTIONS} />
                            </Box>
                        )}
                        {visible('documents') && (
                            <Box sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>Documents</FieldLabel>
                                <RHFSelectField name='documents' label='' placeholder='All' width='100%' size='small' displayEmpty options={DOCUMENTS_OPTIONS} />
                            </Box>
                        )}
                        {selects.map((f) => (
                            <Box key={f.name} sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>{f.label}</FieldLabel>
                                <RHFSelectField name={f.name} label='' placeholder='All' width='100%' size='small' displayEmpty options={f.options} />
                            </Box>
                        ))}
                        {visible('dateRange') && (
                            <Box sx={{ ...dateSx, ...inputSx }}>
                                <FieldLabel>Date Range</FieldLabel>
                                <RHFDateRangePickerField name='fromDate' label='' placeholder='Select date range' size='small' maxDate={new Date()} />
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 auto' }}>
                            <Button
                                type='submit' variant='contained' size='small'
                                sx={{ bgcolor: '#1565c0', color: '#fff', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, '&:hover': { bgcolor: '#0d47a1' } }}
                            >Filter</Button>
                            <Button
                                type='button' variant='contained' size='small' onClick={resetAll}
                                sx={{ bgcolor: '#ef4444', color: '#fff', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, '&:hover': { bgcolor: '#dc2626' } }}
                            >Reset</Button>
                        </Box>
                    </Box>
                </FormProvider>
            </CardContent>
        </Card>
    );
}
